import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { keys, pickBy } from 'lodash';
import { Flex, Input, Text } from '@chakra-ui/react';
import {
  deleteDocumentList,
  postDocumentList,
  updateDocumentList,
} from '../../../api/tabsApi';
import useForm from '../../form/Form';
import { TState, useActions } from '../../../redux/store';
import TreeInput, { TLeaf } from '../../form/TreeInput';
import Modal from '../../modal/Modal';

const inputs = [
  {
    label: 'Nom',
    name: 'name',
    type: 'text',
  },
  {
    label: 'Description',
    name: 'description',
    type: '',
  },
];

export const usePageTree = (withForced?: boolean, noPlanning?: boolean) => {
  const tabs = useSelector((state: TState) => state.tabs);

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState({});

  const checkAll = () => {
    setSelected(
      Object.fromEntries(
        tabs.flatMap((tab) => [
          [`${tab._id}-show`, true],
          ...(tab.menuChilds?.map((c) => [`${c._id}-show`, true]) || []),
        ]),
      ),
    );
  };

  const list: TLeaf[] = [
    {
      id: 'tabs',
      name: 'Pages',
      ignore: true,
      childs: tabs.map((tab) => ({
        id: tab._id,
        name: tab.title,
        icon: tab.icon,
        noSpread: true,
        childs: tab.menuChilds?.map((mc) => ({
          id: mc._id,
          name: mc.title,
          icon: mc.icon,
        })),
      })),
    },
  ];

  if (!noPlanning) {
    list.push({
      id: 'planning',
      name: 'Planning',
    });
  }

  const PageTree = (
    <>
      <Input
        type={'text'}
        onChange={(e) => setFilter(e.target.value)}
        placeholder='Rechercher une page'
      />
      <TreeInput
        list={list}
        actions={[{ title: 'show', label: 'afficher' }]}
        selected={selected}
        onChange={setSelected}
        filter={filter}
        forcedAction={withForced}
      />
    </>
  );

  return { PageTree, selected, setSelected, checkAll };
};

const ManageDocuments = () => {
  const rowData = useSelector((state: TState) => state.rowData);
  const isAdmin = useSelector((state: TState) => state.isAdmin);

  const { onClose, getDocumentList } = useActions();
  const { PageTree, selected, setSelected, checkAll } = usePageTree();

  const isAll = rowData.pages?.includes('tabs-show');

  useEffect(() => {
    if (isAll || !rowData.pages) {
      checkAll();
    } else {
      setSelected(
        rowData.pages?.reduce(
          (prev: any, curr: string) => ({ ...prev, [curr]: true }),
          {},
        ),
      );
    }
  }, [rowData]);

  const { Form, formData, pending } = useForm(inputs);

  const pagesKeys = keys(pickBy(selected, Boolean));
  const pages = pagesKeys.includes('tabs-show') ? ['tabs-show'] : pagesKeys;

  const handleAdd = async () => {
    await postDocumentList({ ...formData, pages });
    getDocumentList({ forced: true });
    onClose();
  };

  const handleUpdate = async () => {
    await updateDocumentList(formData._id, { ...formData, pages });
    getDocumentList({ forced: true });
    onClose();
  };

  const handleDelete = async () => {
    await deleteDocumentList(rowData._id);
    getDocumentList({ forced: true });
    onClose();
  };

  const deletePermission = isAdmin;

  const renderDeleteEntry = <Text>Confirmer la suppression ?</Text>;

  const renderDocumentForm = (
    <Flex gap={3} direction='column'>
      {Form}
      {PageTree}
    </Flex>
  );

  const modalOptions = {
    addDocument: {
      component: renderDocumentForm,
      action: handleAdd,
      title: 'Ajouter document à la liste',
      actionLabel: 'Ajouter',
    },
    editDocument: {
      component: renderDocumentForm,
      canDelete: deletePermission ? 'deleteDocument' : '',
      action: handleUpdate,
      pending,
      title: 'Editer document',
      actionLabel: 'Sauvegarder',
    },
    deleteDocument: {
      component: renderDeleteEntry,
      action: handleDelete,
      title: 'Supprimer un document de la liste',
      actionLabel: 'Confirmer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManageDocuments;
